import { useEffect } from "react";
import { Page, PageStatus } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { Button, Color, Flex, Pagination, SubgridTable, Text } from "src/elements";
import { usePaginationPage } from "src/elements/Pagination";

import {
  ResponseGetInputsMasterFixturesAll,
  RouteGetInputsMasterFixturesAll,
} from "@CommonApi/inputs/master-fixtures/all";
import { useApi } from "src/api";
import { useAsyncState } from "src/hooks/useAsyncState";
import { getFixturesTableColumns } from "./store/columns";
import { useToasts } from "src/components/Toasts";

export const PageFixtures = () => {
  const { getFixturesApi, putInputsMasterFixturesApi } = useApi();
  const { hasLoader } = useLoaders();
  const { toast } = useToasts();

  const [page, setPage] = usePaginationPage();
  const [response, setResponse] = useAsyncState<
    ResponseGetInputsMasterFixturesAll | null | undefined
  >(undefined);

  const isFixturesLoading = response === undefined || hasLoader(RouteGetInputsMasterFixturesAll);

  const handleUpdateFixture = async (
    masterFixtureId: string,
    maxShelfHeight: number,
    maxProductHead: number,
  ) => {
    try {
      await putInputsMasterFixturesApi(masterFixtureId, {
        maxShelfHeight,
        maxProductHead,
      });

      if (response) {
        const updatedData = response.data.map((fixture) =>
          fixture.master_fixture_id === Number(masterFixtureId)
            ? { ...fixture, max_shelf_height: maxShelfHeight, max_product_head: maxProductHead }
            : fixture,
        );
        setResponse({ ...response, data: updatedData });
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      toast({ title: "Failed to update fixture:", error });
    }
  };

  useEffect(() => {
    setResponse(getFixturesApi({ page, size: 20 }));
  }, [page]);

  useEffect(() => {
    return () => {
      setResponse(undefined);
    };
  }, []);

  return (
    <Page>
      <Flex column flexGrow gap="10px">
        <Flex padding="10px 0 0 0" justify="between" align="center">
          <Text variant="h3">Fixtures</Text>

          <Button width="120px" iconRight={{ name: "plus" }} isDisabled>
            Add Fixture
          </Button>
        </Flex>

        <Flex column flexGrow gap="20px">
          {isFixturesLoading && <PageStatus label="Loading Fixtures..." icon="spinner" />}

          {!isFixturesLoading && !response && (
            <PageStatus label="Fixtures not found!" icon="alert" color={Color.red} />
          )}

          {!isFixturesLoading && response && (
            <Flex flexGrow>
              <SubgridTable
                columns={getFixturesTableColumns(handleUpdateFixture)}
                data={response.data}
              />
            </Flex>
          )}
        </Flex>

        <Pagination
          page={page}
          setPage={setPage}
          totalPages={response?.totalPages || 1}
          isUsingInput
          isLoading={response === undefined}
        />
      </Flex>
    </Page>
  );
};
